'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import { isDefined } from '@morpho-org/morpho-ts';
import { CursorPagination, TablePropertiesPopover } from '@morpho-org/ui-v6/client';
import { TableNoResults } from '@morpho-org/ui-v6/server';
import { useTable } from '@tanstack/react-table';
import { startTransition, useState } from 'react';
import { ErrorDataUnavailable } from '@/components/common/Error';
import { Table, TrBodyRow, TrHeaderGroup } from '@/components/common/Table';
import { TableScrollArea } from '@/components/common/Table/CompactTable/Table';
import { TransactionTypeFilter } from '@/components/common/TransactionTypeFilter';
import useTableTransactionData from '@/components/MarketPage/MarketActivitySection/MarketTransactions/hooks/useTableTransactionData';
import { useWeb3Context } from '@/contexts/Web3Context';
import useTableSettings from '@/helpers/cookieStorage/userSettings/tableSettings/client';
import { PageKey } from '@/helpers/cookieStorage/userSettings/types';
import { useIsBelowBreakpoint } from '@/hooks/useBreakpoints';
import { useSkeletonRows } from '@/hooks/useSkeletonRows';
import { TransactionType } from '@/types/legacy';
import { MARKET_YOUR_TRANSACTIONS_DEFAULT_TABLE_SETTINGS, MARKET_YOUR_TRANSACTIONS_STORAGE_KEY, PAGE_SIZE, TRANSACTION_FILTER_OPTIONS } from './config';
import { COLUMN_LABELS, columnsDefs, marketYourTransactionsTableFeatures } from './config/MarketYourTransactions.columns';
import useYourTableTransactionRow from './hooks/useYourTableTransactionRow';
import MarketYourTransactionsLayout from './MarketYourTransactionsLayout';
export interface MarketYourTransactionsClientProps {
  chainId: ChainId;
  marketId: string;
}
const MarketYourTransactionsClient = t0 => {
  const $ = _c(72);
  const {
    chainId,
    marketId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [yourTransactionTypeFilter, setYourTransactionTypeFilter] = useState(t1);
  const {
    account,
    targetChainId
  } = useWeb3Context();
  const userAddress = account.address ?? null;
  const isMediumScreen = useIsBelowBreakpoint("m");
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      pageKey: PageKey.Market,
      tableSettingsObjectKey: MARKET_YOUR_TRANSACTIONS_STORAGE_KEY,
      defaultTableSettings: MARKET_YOUR_TRANSACTIONS_DEFAULT_TABLE_SETTINGS
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    columnVisibility,
    setColumnVisibility
  } = useTableSettings(t2);
  let t3;
  if ($[2] !== yourTransactionTypeFilter) {
    t3 = yourTransactionTypeFilter.map(_temp).filter(isDefined);
    $[2] = yourTransactionTypeFilter;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const activeTransactionTypes = t3;
  let t4;
  if ($[4] !== activeTransactionTypes || $[5] !== chainId || $[6] !== marketId || $[7] !== userAddress) {
    t4 = {
      chainId,
      marketId,
      first: PAGE_SIZE,
      filteredTransactionTypes: activeTransactionTypes,
      userAddress
    };
    $[4] = activeTransactionTypes;
    $[5] = chainId;
    $[6] = marketId;
    $[7] = userAddress;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    items: transactions,
    partial,
    isLoading,
    isLoadingNextPage,
    error,
    pageIndex,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    prefetchNext
  } = useTableTransactionData(t4);
  let t5;
  if ($[9] !== targetChainId || $[10] !== transactions) {
    t5 = {
      transactions,
      chainId: targetChainId
    };
    $[9] = targetChainId;
    $[10] = transactions;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const formattedTransactions = useYourTableTransactionRow(t5);
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = selectedTransactionTypes => {
      startTransition(() => {
        setYourTransactionTypeFilter(selectedTransactionTypes);
      });
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleFilterChange = t6;
  let t7;
  if ($[13] !== columnVisibility) {
    t7 = {
      columnVisibility
    };
    $[13] = columnVisibility;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== formattedTransactions || $[16] !== setColumnVisibility || $[17] !== t7) {
    t8 = {
      features: marketYourTransactionsTableFeatures,
      data: formattedTransactions,
      state: t7,
      columns: columnsDefs,
      enableSorting: false,
      onColumnVisibilityChange: setColumnVisibility
    };
    $[15] = formattedTransactions;
    $[16] = setColumnVisibility;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const transactionsTable = useTable(t8);
  const loading = isLoading || isLoadingNextPage;
  let t9;
  if ($[19] !== loading || $[20] !== transactionsTable) {
    t9 = !loading && transactionsTable.getRowModel().rows.length === 0;
    $[19] = loading;
    $[20] = transactionsTable;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const hasNoTransactions = t9;
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      first: PAGE_SIZE,
      totalCount: PAGE_SIZE,
      pagination: {
        pageIndex: 0,
        pageSize: PAGE_SIZE
      },
      columnsDefs
    };
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const loadingRows = useSkeletonRows(t10);
  let t11;
  if ($[23] !== columnVisibility || $[24] !== transactionsTable) {
    let t12;
    if ($[26] !== columnVisibility) {
      t12 = (column, index) => {
        const label = COLUMN_LABELS[column.id as keyof typeof COLUMN_LABELS];
        if (!isDefined(label)) {
          return null;
        }
        return {
          label,
          value: column.id,
          isActive: isDefined(columnVisibility[column.id]) ? !!columnVisibility[column.id] : true,
          isDisabled: index === 0,
          action: () => {
            column.toggleVisibility();
          }
        };
      };
      $[26] = columnVisibility;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    t11 = transactionsTable.getAllLeafColumns().map(t12).filter(isDefined);
    $[23] = columnVisibility;
    $[24] = transactionsTable;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const columnOptions = t11;
  let t12;
  if ($[28] !== isMediumScreen || $[29] !== yourTransactionTypeFilter) {
    t12 = isMediumScreen ? <div className="gap-xs flex flex-row items-center"><span className="text-3xs text-foreground">Type:</span><TransactionTypeFilter selectedTypes={yourTransactionTypeFilter} onFilterChange={handleFilterChange} options={TRANSACTION_FILTER_OPTIONS} page="market_your_transactions" /></div> : <TransactionTypeFilter selectedTypes={yourTransactionTypeFilter} onFilterChange={handleFilterChange} options={TRANSACTION_FILTER_OPTIONS} page="market_your_transactions" />;
    $[28] = isMediumScreen;
    $[29] = yourTransactionTypeFilter;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== columnOptions) {
    t13 = <TablePropertiesPopover options={columnOptions} />;
    $[31] = columnOptions;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t12 || $[34] !== t13) {
    t14 = <div className="gap-sm max-lg:gap-xs flex flex-row items-center">{t12}{t13}</div>;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== hasNoTransactions || $[37] !== loading || $[38] !== loadingRows || $[39] !== transactionsTable) {
    t15 = (!hasNoTransactions || loading) && <Table.Table tableVariant="compact"><Table.Thead>{transactionsTable.getHeaderGroups().map(_temp2)}</Table.Thead><Table.Tbody>{!loading && transactionsTable.getRowModel().rows.map(_temp3)}{loading && loadingRows.map(_temp4)}</Table.Tbody></Table.Table>;
    $[36] = hasNoTransactions;
    $[37] = loading;
    $[38] = loadingRows;
    $[39] = transactionsTable;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== error || $[42] !== hasNoTransactions || $[43] !== loading) {
    t16 = hasNoTransactions && !error && !loading && <TableNoResults text="No transactions found." height="478px" style={{
      borderRadius: "0"
    }} />;
    $[41] = error;
    $[42] = hasNoTransactions;
    $[43] = loading;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== error || $[46] !== loading) {
    t17 = !loading && error && <ErrorDataUnavailable minHeight="340px" style={{
      borderRadius: "0"
    }} />;
    $[45] = error;
    $[46] = loading;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== t15 || $[49] !== t16 || $[50] !== t17) {
    t18 = <TableScrollArea className="grid h-full w-full rounded-[20px]">{t15}{t16}{t17}</TableScrollArea>;
    $[48] = t15;
    $[49] = t16;
    $[50] = t17;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== error || $[53] !== loading || $[54] !== partial) {
    t19 = partial && !loading && !error && <p className="text-secondary-foreground px-sm text-xs" data-testid="market-your-transactions-partial-notice">Some transactions could not be fully loaded, so this page may be incomplete.</p>;
    $[52] = error;
    $[53] = loading;
    $[54] = partial;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  const t20 = loading && (canPreviousPage || canNextPage);
  let t21;
  if ($[56] !== canNextPage || $[57] !== canPreviousPage || $[58] !== error || $[59] !== nextPage || $[60] !== pageIndex || $[61] !== prefetchNext || $[62] !== previousPage || $[63] !== t20) {
    t21 = <CursorPagination loading={t20} error={error} pageIndex={pageIndex} previousPage={previousPage} nextPage={nextPage} canPreviousPage={canPreviousPage} canNextPage={canNextPage} onPrefetchNext={prefetchNext} />;
    $[56] = canNextPage;
    $[57] = canPreviousPage;
    $[58] = error;
    $[59] = nextPage;
    $[60] = pageIndex;
    $[61] = prefetchNext;
    $[62] = previousPage;
    $[63] = t20;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  let t22;
  if ($[65] !== t18 || $[66] !== t19 || $[67] !== t21) {
    t22 = <div className="gap-lg flex h-full w-full flex-col"><div className="gap-xs flex flex-col">{t18}{t19}{t21}</div></div>;
    $[65] = t18;
    $[66] = t19;
    $[67] = t21;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  let t23;
  if ($[69] !== t14 || $[70] !== t22) {
    t23 = <MarketYourTransactionsLayout tableSettingsSlot={t14}>{t22}</MarketYourTransactionsLayout>;
    $[69] = t14;
    $[70] = t22;
    $[71] = t23;
  } else {
    t23 = $[71];
  }
  return t23;
};
export default MarketYourTransactionsClient;
function _temp(t) {
  return TransactionType[t as keyof typeof TransactionType];
}
function _temp2(headerGroup) {
  return <TrHeaderGroup key={headerGroup.id} headerGroup={headerGroup} />;
}
function _temp3(row) {
  return <TrBodyRow key={`${row.id}-${row.original.type}`} row={row} />;
}
function _temp4(row_0, index_0) {
  return <TrBodyRow key={`skeleton-${index_0}`} skeletonRow={row_0} />;
}